import { useCallback, useEffect, useState } from 'react';

// The Hazcheck theme switches every colour token on a `light-mode` or `dark-mode`
// class on <html>, the same contract the Vue/Quasar products use, so a user who
// keeps one of them dark gets this app dark under the same localStorage key.
export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'theme';

export function loadTheme(): ThemeMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function applyTheme(mode: ThemeMode): void {
  const html = document.documentElement;
  html.classList.remove('light-mode', 'dark-mode');
  html.classList.add(`${mode}-mode`);
  html.style.colorScheme = mode;
}

function saveTheme(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Private windows and blocked storage still get the mode for this page load
  }
}

export function useTheme(): [ThemeMode, () => void] {
  const [mode, setMode] = useState<ThemeMode>(loadTheme);

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((current) => {
      const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
      saveTheme(next);
      return next;
    });
  }, []);

  return [mode, toggle];
}
